import type { GamepadAnalogBinding, GamepadMapping } from './game/gamepad.js';
import { keyboardBindings, keyCodeLabel, type KeyAction } from './keyboard-bindings.js';
import { t } from './i18n/index.js';

/**
 * Controller-family aware button glyphs. The Gamepad API's standard mapping
 * numbers buttons by position, so index 0 is always the bottom face button:
 * A on Xbox, Cross on PlayStation and B on Nintendo layouts.
 */
export type PadFamily = 'xbox' | 'playstation' | 'nintendo' | 'generic';

export type PadAction =
  | 'steer' | 'throttle' | 'brake' | 'handbrake' | 'nitro' | 'fire'
  | 'camera' | 'turret' | 'reset' | 'bank' | 'settings';

interface GlyphSpec {
  label: string;
  /** Visual shape used by the CSS: round face button, bumper, trigger, stick, d-pad or system pill. */
  kind: 'face' | 'bumper' | 'trigger' | 'stick' | 'dpad' | 'system';
  /** Face-button slot, which owns the colour accent. */
  slot?: 'south' | 'east' | 'west' | 'north';
}

const PLAYSTATION_FACE = ['✕', '○', '□', '△'];
const XBOX_FACE = ['A', 'B', 'X', 'Y'];
const NINTENDO_FACE = ['B', 'A', 'Y', 'X'];
const FACE_SLOTS = ['south', 'east', 'west', 'north'] as const;

export function detectPadFamily(id: string): PadFamily {
  const value = id.toLowerCase();
  if (/045e|xbox|xinput|x-box|microsoft/.test(value)) return 'xbox';
  if (/054c|playstation|dualshock|dualsense|ps[345]/.test(value)) return 'playstation';
  if (/057e|nintendo|switch|pro controller|joy-?con/.test(value)) return 'nintendo';
  // Sony pads report a bare "Wireless Controller" on several platforms.
  if (/^wireless controller/.test(value)) return 'playstation';
  // Most unknown pads in a browser expose the Xbox-style standard mapping.
  return value.includes('standard gamepad') ? 'xbox' : 'generic';
}

export function padFamilyName(family: PadFamily): string {
  return family === 'playstation' ? 'PLAYSTATION' : family === 'nintendo' ? 'NINTENDO' : family === 'xbox' ? 'XBOX' : t('CONTROLLER');
}

function glyphSpec(index: number, family: PadFamily): GlyphSpec {
  if (index >= 0 && index <= 3) {
    const face = family === 'playstation' ? PLAYSTATION_FACE : family === 'nintendo' ? NINTENDO_FACE : XBOX_FACE;
    return { label: face[index]!, kind: 'face', slot: FACE_SLOTS[index] };
  }
  const ps = family === 'playstation';
  const nin = family === 'nintendo';
  switch (index) {
    case 4: return { label: ps ? 'L1' : nin ? 'L' : 'LB', kind: 'bumper' };
    case 5: return { label: ps ? 'R1' : nin ? 'R' : 'RB', kind: 'bumper' };
    case 6: return { label: ps ? 'L2' : nin ? 'ZL' : 'LT', kind: 'trigger' };
    case 7: return { label: ps ? 'R2' : nin ? 'ZR' : 'RT', kind: 'trigger' };
    case 8: return { label: ps ? 'CREATE' : nin ? '−' : 'VIEW', kind: 'system' };
    case 9: return { label: ps ? 'OPTIONS' : nin ? '+' : 'MENU', kind: 'system' };
    case 10: return { label: ps ? 'L3' : 'LS', kind: 'stick' };
    case 11: return { label: ps ? 'R3' : 'RS', kind: 'stick' };
    case 12: return { label: '▲', kind: 'dpad' };
    case 13: return { label: '▼', kind: 'dpad' };
    case 14: return { label: '◀', kind: 'dpad' };
    case 15: return { label: '▶', kind: 'dpad' };
    case 16: return { label: ps ? 'PS' : nin ? 'HOME' : 'GUIDE', kind: 'system' };
    default: return { label: `B${index}`, kind: 'system' };
  }
}

export function buttonLabel(index: number, family: PadFamily): string {
  return glyphSpec(index, family).label;
}

export function buttonGlyphHtml(index: number, family: PadFamily, extraClass = ''): string {
  const spec = glyphSpec(index, family);
  return `<span class="pad-glyph${extraClass ? ` ${extraClass}` : ''}" data-kind="${spec.kind}"${spec.slot ? ` data-slot="${spec.slot}"` : ''} data-family="${family}">${spec.label}</span>`;
}

export function stickGlyphHtml(side: 'left' | 'right', family: PadFamily): string {
  const label = side === 'left' ? family === 'playstation' ? 'L' : 'LS' : family === 'playstation' ? 'R' : 'RS';
  return `<span class="pad-glyph" data-kind="stick" data-family="${family}">${label}</span>`;
}

function axisGlyphHtml(axis: number, family: PadFamily, suffix = ''): string {
  if (axis === 0 || axis === 1) return stickGlyphHtml('left', family);
  if (axis === 2 || axis === 3) return stickGlyphHtml('right', family);
  return `<span class="pad-glyph" data-kind="stick" data-family="${family}">AX${axis}${suffix}</span>`;
}

export function analogBindingGlyphHtml(binding: GamepadAnalogBinding, family: PadFamily): string {
  if (binding.source === 'button') return buttonGlyphHtml(binding.index, family);
  const suffix = binding.source === 'axis-positive' ? '+' : binding.source === 'axis-negative' ? '−' : '';
  return `<span class="pad-glyph" data-kind="trigger" data-family="${family}">AX${binding.index}${suffix}</span>`;
}

/** Glyph markup for one race action under the current controller mapping. */
export function padActionGlyphHtml(action: PadAction, mapping: GamepadMapping, family: PadFamily): string {
  switch (action) {
    case 'steer': return axisGlyphHtml(mapping.steerAxis, family);
    case 'turret': return axisGlyphHtml(mapping.turretAxis, family);
    case 'throttle': return analogBindingGlyphHtml(mapping.throttle, family);
    case 'brake': return analogBindingGlyphHtml(mapping.brake, family);
    case 'handbrake': return buttonGlyphHtml(mapping.handbrakeButton, family);
    case 'nitro': return buttonGlyphHtml(mapping.nitroButton, family);
    case 'fire': return buttonGlyphHtml(mapping.fireButton, family);
    case 'camera': return buttonGlyphHtml(mapping.cameraButton, family);
    case 'reset': return buttonGlyphHtml(mapping.resetButton, family);
    case 'bank': return buttonGlyphHtml(10, family);
    case 'settings': return buttonGlyphHtml(mapping.settingsButton, family);
  }
}

export function keyCapHtml(code: string, extraClass = ''): string {
  const label = keyCodeLabel(code);
  const wide = label.length > 2 ? ' wide' : '';
  return `<kbd class="key-cap${wide}${extraClass ? ` ${extraClass}` : ''}">${label}</kbd>`;
}

/** Keycaps for an action: primary only by default, so hints stay compact. */
export function keyActionHtml(action: KeyAction, includeSecondary = false): string {
  const binding = keyboardBindings()[action];
  const primary = keyCapHtml(binding.primary);
  return includeSecondary && binding.secondary ? `${primary}${keyCapHtml(binding.secondary, 'secondary')}` : primary;
}

/**
 * Rewrites every `[data-pad-action]` and `[data-key-action]` placeholder under
 * `root`. `data-key-action` accepts a space-separated list so a hint such as
 * DRIVE can show W A S D from the live bindings.
 */
export function renderInputGlyphs(root: ParentNode, mapping: GamepadMapping, family: PadFamily): void {
  for (const element of root.querySelectorAll<HTMLElement>('[data-pad-action]')) {
    const action = element.dataset.padAction as PadAction;
    const html = padActionGlyphHtml(action, mapping, family);
    if (element.innerHTML !== html) element.innerHTML = html;
  }
  for (const element of root.querySelectorAll<HTMLElement>('[data-pad-button]')) {
    const html = buttonGlyphHtml(Number(element.dataset.padButton), family);
    if (element.innerHTML !== html) element.innerHTML = html;
  }
  for (const element of root.querySelectorAll<HTMLElement>('[data-key-action]')) {
    const actions = (element.dataset.keyAction ?? '').split(/\s+/).filter(Boolean) as KeyAction[];
    const html = actions.map((action) => keyActionHtml(action)).join('');
    if (element.innerHTML !== html) element.innerHTML = html;
  }
}
